import Skeleton from 'react-loading-skeleton';
import type { CheckboxProps } from 'antd';
import { Select, Popover, Checkbox, Space } from 'antd';
import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './search-gas-price.scss';
import type { IGasPriceModel, IGetlocalizedMessage, IHandleNetworkChange, IHandleTokenChange, ITokens, ITokenOption, IToken } from 'src/interfaces/gas-price.model';
import type { ILabelInValueType } from 'src/interfaces/network.model';
import { updateUrlParam } from '../../helpers/updateUrlParams';

export default function SearchGasPrice({
  gasPriceData,
  handleTokenChange,
  loading,
  localizedMessage,
  tokensData,
  currentNetwork,
  handleNetworkChange,
}: {
  gasPriceData: IGasPriceModel | null;
  loading: boolean;
  localizedMessage: IGetlocalizedMessage;
  tokensData: ITokens | null;
  handleTokenChange: IHandleTokenChange;
  currentNetwork: string | null;
  handleNetworkChange: IHandleNetworkChange;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNativeSelectedFromUrl = searchParams.get('Native') === 'true';
  const tokenIdFromUrl = searchParams.get('tokenId');
  const contentPopover = localizedMessage('calculator__popover-text');
  const [open, setOpen] = useState(false);
  const [isNativeSelected, setIsNativeSelected] = useState(false);
  const sortedTokens = tokensData?.tokens?.sort((a: IToken, b: IToken) => a.id - b.id) || [];
  const [selectedToken, setSelectedToken] = useState<string | undefined>(() => sortedTokens.find((t) => t.tokenId.toString() === tokenIdFromUrl)?.name || gasPriceData?.name);

  const isDisableSelect: boolean = Boolean(isNativeSelected) || !(sortedTokens.length > 0);
  const isDisableCheckbox: boolean = !(sortedTokens.length > 0);
  const optionValue: ITokenOption[] = sortedTokens.map((token: IToken) => ({
    key: `${token.name}-${token.tokenId}`,
    value: token.name,
    symbol: token.symbol,
    tokenId: token.tokenId,
    logo: token.logo,
  }));
  const labelRender = (props: ILabelInValueType) => {
    const { label, value } = props;
    if (label) {
      return label;
    }
    return value;
  };
  const onChange: CheckboxProps['onChange'] = (e) => {
    const { checked } = e.target;
    setIsNativeSelected(checked);
    updateUrlParam('Native', checked ? 'true' : 'false', navigate);

    if (checked) {
      updateUrlParam('tokenId', null, navigate);
      setSelectedToken(gasPriceData?.name);
      handleNetworkChange(String(currentNetwork));
    }
  };

  const renderOption = (originalOption: { data: ITokenOption }) => (
    <Space className="search-gas-price__list">
      <span className="search-gas-price__list--item">
        <img src={originalOption.data.logo} alt="logo" className="search-gas-price__logo skeleton" />
        {originalOption.data.value}
      </span>
      <span className="search-gas-price__list--symbol">{originalOption.data.symbol}</span>
    </Space>
  );
  const handleSelectChange = (value: string, option: ITokenOption) => {
    updateUrlParam('tokenId', option.tokenId.toString(), navigate);
    setSelectedToken(value);
    handleTokenChange(option.tokenId);
  };
  useEffect(() => {
    if (isDisableCheckbox) {
      setIsNativeSelected(true);
    } else {
      setIsNativeSelected(isNativeSelectedFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTokens.length, currentNetwork, isDisableCheckbox]);

  useEffect(() => {
    if (isNativeSelected || isDisableCheckbox) {
      setSelectedToken(gasPriceData?.name);
      handleNetworkChange(String(currentNetwork));
      return;
    }

    const matchedToken = sortedTokens.find((t) => t.tokenId.toString() === tokenIdFromUrl);

    if (matchedToken) {
      setSelectedToken(matchedToken.name);
      handleTokenChange(matchedToken.tokenId);
    } else {
      setSelectedToken(gasPriceData?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeSelected, isDisableCheckbox, tokenIdFromUrl, sortedTokens, gasPriceData?.name, currentNetwork]);

  return (
    <>
      <h4 className="calculator__network-header">{loading ? <Skeleton /> : localizedMessage('calculator__choosing-token')}</h4>
      <div className="search-gas-price__content--token">
        <Select
          size="large"
          options={optionValue}
          labelRender={labelRender}
          showSearch
          value={selectedToken}
          onDropdownVisibleChange={(value) => setOpen(value)}
          defaultActiveFirstOption
          className="search-gas-price__select"
          onChange={handleSelectChange}
          suffixIcon={
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(!open);
                }
              }}
            >
              <DownOutlined
                style={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#9498A5',
                  fontSize: '16px',
                }}
              />
            </div>
          }
          disabled={isDisableSelect}
          open={open}
          optionRender={renderOption}
        />
        <div className="search-gas-price__content-checkbox">
          <Popover placement="right" content={contentPopover} className="search-gas-price__pop-over">
            <InfoCircleOutlined className="search-gas-price__info-circle" style={{ color: '#9F9F9F' }} />
          </Popover>
          <Checkbox className="search-gas-price__checkbox" disabled={isDisableCheckbox} onChange={onChange} checked={isNativeSelected}>
            {loading ? <Skeleton inline width={160} /> : localizedMessage('calculator__checkbox-text')}
          </Checkbox>
        </div>
      </div>
    </>
  );
}
